import { inject, Injectable } from '@angular/core';
import { EvaluateBlockVisibilityService } from '@core/services/content-builder/evaluate-block-visibility.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  CART_FUNNEL_STORE_GENERATED_ACTIONS,
  CART_FUNNEL_STORE_LOCAL_ACTIONS,
  CartFunnelFormBlock,
} from '@store/cart-funnel';
import {
  SELECT_CART_FUNNEL_ACTIVE_STEP_ID,
  SELECT_CART_FUNNEL_STEPS_ALL,
} from '@store/cart-funnel/cart-funnel.selection';
import { SELECT_CONTENT_BLOCKS_ALL } from '@store/content';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CartFunnelEffects {
  determineCartFunnelDisplayableStepsEffect = createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      evaluateBlockVisibilityService = inject(EvaluateBlockVisibilityService),
    ) => {
      return actions$.pipe(
        ofType(CART_FUNNEL_STORE_LOCAL_ACTIONS.addStep),
        switchMap(() => store.select(SELECT_CART_FUNNEL_STEPS_ALL)),
        switchMap(allStepsBlockIds =>
          store.select(SELECT_CONTENT_BLOCKS_ALL).pipe(
            switchMap(allBlocks => {
              // Build an array of observables for each block visibility
              const funnelFormBlocksWithVisibility = allStepsBlockIds
                .map(
                  ({ id: stepBlockId }) =>
                    allBlocks.find(block => block.id === stepBlockId) as CartFunnelFormBlock,
                )
                .map(stepBlock => {
                  const items = stepBlock?.data?.items?.map(item =>
                    allBlocks.find(block => block.id === item.block),
                  );
                  return {
                    ...stepBlock,
                    data: {
                      ...stepBlock.data,
                      items,
                    },
                  };
                })
                .filter(stepBlock => stepBlock !== undefined)
                .map(stepBlock => {
                  const stepExpression =
                    stepBlock?.data?.common?.conditionalVisibility?.expression ?? 'true';

                  // Evaluate each item's visibility individually
                  const itemVisibilityObservables =
                    stepBlock?.data?.items?.map(item => {
                      const itemExpression =
                        item?.data?.common?.conditionalVisibility?.expression ?? 'true';
                      return evaluateBlockVisibilityService
                        .evaluate$(itemExpression, {
                          additionalContext: stepBlock?.data?.common?.variables,
                        })
                        .pipe(
                          distinctUntilChanged(),
                          map(expressionResult => !!expressionResult),
                        );
                    }) || [];

                  // Combine step visibility with items visibility
                  return combineLatest([
                    evaluateBlockVisibilityService.evaluate$(stepExpression, {
                      additionalContext: stepBlock?.data?.common?.variables,
                    }),
                    ...itemVisibilityObservables,
                  ]).pipe(
                    distinctUntilChanged(),
                    map(([stepVisible, ...itemsVisible]) => {
                      // Step is visible only if the step itself is visible AND at least one item is visible
                      const hasVisibleItems = itemsVisible.some(itemVisible => itemVisible);
                      const isStepVisible = !!stepVisible && hasVisibleItems;

                      return {
                        ...stepBlock,
                        isStepVisible,
                      };
                    }),
                  );
                });

              // Combine all block observables into one observable emitting an array
              return combineLatest(funnelFormBlocksWithVisibility);
            }),
            takeUntil(actions$.pipe(ofType(CART_FUNNEL_STORE_LOCAL_ACTIONS.leaveCartFunnel))),
          ),
        ),
        map(allStepsBlocks =>
          CART_FUNNEL_STORE_LOCAL_ACTIONS.setDisplayableStepsIds({
            displayableStepsIds: allStepsBlocks
              .filter(block => block.isStepVisible)
              .map(block => block.id) as string[],
          }),
        ),
      );
    },
  );

  handleInitialCartFunnelActiveStepEffect = createEffect(
    (actions$ = inject(Actions), store = inject(Store)) => {
      return actions$.pipe(
        ofType(CART_FUNNEL_STORE_LOCAL_ACTIONS.setDisplayableStepsIds),
        withLatestFrom(store.select(SELECT_CART_FUNNEL_ACTIVE_STEP_ID)),
        filter(([_, activeStepId]) => !activeStepId), // Only continue if no active step
        map(([action]) => {
          return CART_FUNNEL_STORE_GENERATED_ACTIONS.setActive({
            active: action.displayableStepsIds[0],
          });
        }),
      );
    },
  );
}
